import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Scale, Briefcase, Building, ArrowRight } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';

const AssistantsIndustries: React.FC = () => {
  const industries = [
    {
      icon: Calculator,
      name: 'Accounting',
      slug: 'accounting',
      description: 'AI assistants for accounting firms, bookkeepers, and financial advisors.',
      useCases: ['Client onboarding', 'Tax deadline reminders', 'Software support', 'Document requests'],
      available: true,
    },
    {
      icon: Scale,
      name: 'Legal',
      slug: 'legal',
      description: 'AI assistants for law firms and legal service providers.',
      useCases: ['Case status updates', 'Document preparation', 'Appointment scheduling', 'FAQ handling'],
      available: false,
    },
    {
      icon: Briefcase,
      name: 'Professional Services',
      slug: 'professional-services',
      description: 'AI assistants for consultants, advisors, and service businesses.',
      useCases: ['Project updates', 'Resource allocation', 'Client communication', 'Proposal support'],
      available: false,
    },
    {
      icon: Building,
      name: 'Financial Services',
      slug: 'financial-services',
      description: 'AI assistants for financial planners and wealth managers.',
      useCases: ['Portfolio queries', 'Compliance questions', 'Meeting preparation', 'Market updates'],
      available: false,
    },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand-navy to-brand-blue py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
                Industries We Serve
              </h1>
              <p className="text-xl text-white/90">
                Advisory Assistants are built with deep industry expertise. 
                Each solution includes governance frameworks and templates designed for your sector.
              </p>
            </div>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {industries.map((industry) => (
                <div 
                  key={industry.slug}
                  className={`bg-card border rounded-xl p-8 ${
                    industry.available ? 'border-brand-teal' : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-brand-teal/10 rounded-lg">
                      <industry.icon className="h-7 w-7 text-brand-teal" />
                    </div>
                    {industry.available ? (
                      <span className="text-xs font-medium bg-brand-teal/10 text-brand-teal px-3 py-1 rounded-full">
                        Available Now
                      </span>
                    ) : (
                      <span className="text-xs font-medium bg-muted text-muted-foreground px-3 py-1 rounded-full">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <h3 className="text-2xl font-semibold mb-3 text-card-foreground">{industry.name}</h3>
                  <p className="text-muted-foreground mb-6">{industry.description}</p>
                  <div className="mb-6">
                    <p className="text-sm font-medium text-card-foreground mb-2">Common Use Cases:</p>
                    <ul className="grid grid-cols-2 gap-2">
                      {industry.useCases.map((useCase, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-brand-teal rounded-full" />
                          {useCase}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {industry.available ? (
                    <Link to={`/assistants/industries/${industry.slug}`}>
                      <Button className="w-full">
                        Explore {industry.name}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  ) : (
                    <Button disabled className="w-full">
                      Coming Soon
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-20 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
              Don't See Your Industry?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              We're expanding to new sectors. Contact us to discuss a custom Advisory Assistant for your industry.
            </p>
            <Link to="/assistants/get-assistant">
              <Button size="lg">
                Get in Touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AssistantsIndustries;
